import React,{ useState, useEffect }from 'react';
import { auth, db } from './utils/firebase';
import { setUserSearching } from './utils/firebase'; // NEW: Import the function to mark user as searching
import HeaderContainer from './components/layout/HeaderContainer';
import Navigation from './components/layout/Navigation';
import LoginView from './components/auth/LoginView';
import AgendaView from './components/agenda/AgendaView';
import HomeView from './components/trails/HomeView';
import TrailDetailView from './components/trails/TrailDetailView';
import MatchingView from './components/matches/MatchingView';
import NoMatchesView from './components/matches/NoMatchesView';
import MatchesView from './components/matches/MatchesView';
import MatchesGalleryView from './components/matches/MatchesGalleryView';
import ProfileView from './components/profile/ProfileView';
import RequestSentView from './components/matches/RequestSentView';
import MatchAcceptedView from './components/matches/MatchAcceptedView';
import PersonalProfileView from './components/profile/PersonalProfileView';
import { onSnapshot } from 'firebase/firestore';

import { 
    collection, 
    doc, 
    getDoc, 
    getDocs, 
    query, 
    where, 
    updateDoc, 
    addDoc, 
    setDoc
} from 'firebase/firestore';
import emailjs from '@emailjs/browser';
import PastMatchFeedbackModal from './components/matches/PastMatchFeedbackModal';
import OnboardingView from './components/onboarding/OnboardingView';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import AdminTrailManager from './components/admin/AdminTrailManager';
import AdminRoute from './components/admin/AdminRoute';
//import { migrateUserSkillLevels } from './utils/migrations/migrateSkillLevels';
//import { updateExistingTrailsActivity } from './utils/databaseUtils';

// Initialize EmailJS with public key from environment variables
emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);

const calculatePreferenceCompatibility = (user1, user2) => {
    let score = 0;
    if (user1.preferences?.pace === user2.preferences?.pace) score += 20;
    if (user1.preferences?.communication === user2.preferences?.communication) score += 15;
    if (user1.preferences?.adventureLevel === user2.preferences?.adventureLevel) score += 15;
    return score;
};

const calculateCompatibility = (user1, user2, activity) => {
    const user1Skill = user1.skills?.[activity] || 'beginner';
    const user2Skill = user2.skills?.[activity] || 'beginner';
    const skillLevels = { beginner: 1, intermediate: 2, advanced: 3 };
    const skillDiff = Math.abs(skillLevels[user1Skill] - skillLevels[user2Skill]);
    const skillCompatibility = (1 - skillDiff / 2) * 100;
    const preferenceCompatibility = calculatePreferenceCompatibility(user1, user2);
    return Math.round((skillCompatibility + preferenceCompatibility) / 2);
};

const App = () => {
    const [currentView, setCurrentView] = React.useState('home');
    const [selectedTrail, setSelectedTrail] = React.useState(null);
    const [selectedProfile, setSelectedProfile] = React.useState(null);
    const [trails, setTrails] = React.useState([]);
    const [profiles, setProfiles] = React.useState([]);
    const [matches, setMatches] = React.useState([]);
    const [user, setUser] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [potentialMatches, setPotentialMatches] = React.useState([]);
    const [pastMatchToReview, setPastMatchToReview] = React.useState(null);
    const [showOnboarding, setShowOnboarding] = React.useState(false);
    const [requireAuth, setRequireAuth] = React.useState(false);
    window.setRequireAuth = setRequireAuth; // ✅ Make it callable globally
    const [trailViewSource, setTrailViewSource] = React.useState('home');
    const [selectedProfileSource, setSelectedProfileSource] = React.useState('matches');
    const [activeView, setActiveView] = useState('home'); // or 'matches', etc.
    const [showProfile, setShowProfile] = useState(false);
    const [selectedMatchTab, setSelectedMatchTab] = useState('confirmed'); 

    // NEW: New function to handle "Find Hiking Buddy" that marks the user as searching for the current trail and redirects to NoMatchesView.
    const handleFindHikingBuddy = (trail) => {
        setSelectedTrail(trail);
        setCurrentView('no-matches');
      };
      

    React.useEffect(() => {
        const fetchTrails = async () => {
            try {
                const trailsSnapshot = await getDocs(collection(db, 'trails'));
                const trailsData = trailsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setTrails(trailsData);
            } catch (error) {
                console.error("Error fetching trails:", error);
            }
        };
        fetchTrails();
    }, []);

    React.useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                const userDocRef = doc(db, 'users', user.uid);
                let userDocSnap = await getDoc(userDocRef);
                
                // If user document doesn't exist, create it and re-fetch
                if (userDocSnap.exists()) {
                    setUser({ ...user, ...userDocSnap.data() });
                } else {
                    setUser(user); // allow onboarding to proceed to completion form
                }
                const handleOnboardingComplete = async () => {
                    if (user) {
                        const userDocRef = doc(db, 'users', user.uid);
                        await updateDoc(userDocRef, {
                            hasSeenOnboarding: true,
                            skills: user.skills || { hiking: 'beginner', running: 'beginner' }  // ✅ always include this
                        });
                    }
                    setShowOnboarding(false);
                };                
    
                setUser({ ...user, ...userDocSnap.data() });
            } else {
                setUser(null); 
            }
            setLoading(false);
        });
    
        return () => unsubscribe();
    }, []);
    

    React.useEffect(() => {
        if (!user || !user.uid) return;
      
        const fetchData = async () => {
          try {
            const profilesSnapshot = await getDocs(collection(db, 'users'));
            const profilesData = profilesSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setProfiles(profilesData);
      
            const matchesRef = collection(db, 'matches');
      
            const [userMatches1, userMatches2, searchingMatches] = await Promise.all([
              getDocs(query(matchesRef, where('profile_ids.id1', '==', user.uid))),
              getDocs(query(matchesRef, where('profile_ids.id2', '==', user.uid))),
              getDocs(query(matchesRef, where('status', '==', 'searching')))
            ]);
      
            const allDocs = [
              ...userMatches1.docs,
              ...userMatches2.docs,
              ...searchingMatches.docs
            ];
      
            const uniqueDocs = Array.from(
              new Map(allDocs.map(doc => [doc.id, doc])).values()
            );
      
            const matchesData = uniqueDocs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setMatches(matchesData);
          } catch (error) {
            console.error("Error fetching data:", error);
          }
        };
      
        fetchData();
      
        // ✅ real-time syncing using onSnapshot
        const matchesRef = collection(db, 'matches');
        const q1 = query(matchesRef, where('profile_ids.id1', '==', user.uid));
        const q2 = query(matchesRef, where('profile_ids.id2', '==', user.uid));
      
        const handleSnapshot = (snapshot) => {
          const updatedMatches = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setMatches(prev => {
            const otherMatches = prev.filter(m => !updatedMatches.find(n => n.id === m.id));
            return [...otherMatches, ...updatedMatches];
          });
        };
      
        const unsubscribe1 = onSnapshot(q1, handleSnapshot);
        const unsubscribe2 = onSnapshot(q2, handleSnapshot);
      
        return () => {
          unsubscribe1();
          unsubscribe2();
        };
      }, [user]);
      

    // (The rest of your handler functions remain unchanged.)
    const handleMatch = async () => {
        if (!user) {
            setRequireAuth(true);
            return;
        }
        setCurrentView('matching');
        const activity = selectedTrail.activity || 'hiking';
        try {
            const matchesRef = collection(db, 'matches');
            const existingMatchQuery1 = query(
                matchesRef,
                where('trail_id', '==', selectedTrail.id.toString()),
                where('status', 'in', ['pending', 'searching']),
                where('profile_ids.id1', '==', user.uid)
            );
            const existingMatchQuery2 = query(
                matchesRef,
                where('trail_id', '==', selectedTrail.id.toString()),
                where('status', 'in', ['pending', 'searching']),
                where('profile_ids.id2', '==', user.uid)
            );
            const [existingMatchDocs1, existingMatchDocs2] = await Promise.all([
                getDocs(existingMatchQuery1),
                getDocs(existingMatchQuery2)
            ]);
            if (!existingMatchDocs1.empty || !existingMatchDocs2.empty) {
                setTimeout(() => setCurrentView('no-matches'), 1000);
                return;
            }
            const q = query(
                matchesRef,
                where('trail_id', '==', selectedTrail.id.toString()),
                where('status', '==', 'searching'),
                where('profile_ids.id2', '==', null)
            );
            const pendingMatchesSnapshot = await getDocs(q);
            if (pendingMatchesSnapshot.empty) {
                await createNewMatch();
                return;
            } else {
                const matchDoc = pendingMatchesSnapshot.docs[0];
                const match = matchDoc.data();
                
                // Only update id2 if the user is not already id1
                if (match.profile_ids?.id1 !== user.uid) {
                    await updateDoc(doc(db, 'matches', matchDoc.id), {
                        'profile_ids.id2': user.uid,
                        'status': 'pending',
                        'participants': [match.profile_ids.id1, user.uid]

                      });
                      
                }
                
                setMatches(prevMatches =>
                    prevMatches.map(match =>
                        match.id === matchDoc.id
                            ? { ...match, profile_ids: { ...match.profile_ids, id2: user.uid }, status: 'pending' }
                            : match
                    )
                );
                setSelectedProfile({ name: 'Your new hiking buddy' });
                setCurrentView('request-sent');
                return;
            }
        } catch (error) {
            console.error("Error in matching:", error);
            setTimeout(() => setCurrentView('no-matches'), 1000);
        }
    };

    const createNewMatch = async () => {
        const newMatch = {
            sport: 'Hiking',
            profile_ids: {
              id1: user.uid,
              id2: null
            },
            trail_id: selectedTrail.id.toString(),
            status: 'searching',
            start_time: selectedTrail.start_time,
            end_time: selectedTrail.end_time,
            compatibility: null,
            participants: [user.uid] // ✅ This will match the rules expecting the requester to be a participant
          };
          
        const matchDocId = `${selectedTrail.id}_${user.uid}`;
        await setDoc(doc(db, 'matches', matchDocId), newMatch); 
        setMatches(prevMatches => {
            const filtered = prevMatches.filter(match => match.id !== matchDocId);
            return [...filtered, { id: matchDocId, ...newMatch }];
        });
        setTimeout(() => setCurrentView('no-matches'), 1000);
    };

    const handleSendRequest = async () => {
        if (!user) {
            setRequireAuth(true);
            return;
        }
        try {
            const matchesRef = collection(db, 'matches');
            const existingMatchQuery1 = query(
                matchesRef,
                where('profile_ids.id1', '==', user.uid),
                where('profile_ids.id2', '==', selectedProfile.id),
                where('status', 'in', ['pending', 'confirmed'])
            );
            const existingMatchQuery2 = query(
                matchesRef,
                where('profile_ids.id1', '==', selectedProfile.id),
                where('profile_ids.id2', '==', user.uid),
                where('status', 'in', ['pending', 'confirmed'])
            );
            const [existingMatches1, existingMatches2] = await Promise.all([
                getDocs(existingMatchQuery1),
                getDocs(existingMatchQuery2)
            ]);
            if (!existingMatches1.empty || !existingMatches2.empty) {
                setCurrentView('request-sent');
                return;
            }
    
            const searchingMatchQuery = query(
                matchesRef,
                where('trail_id', '==', selectedTrail.id.toString()),
                where('status', '==', 'searching')
            );
            const searchingMatchSnapshot = await getDocs(searchingMatchQuery);
            const matchDoc = searchingMatchSnapshot.docs[0];
            if (!matchDoc) {
              console.error("No searching match found");
              return;
            }
            
            const trailId = matchDoc.data().trail_id;
            const matchTrail = trails.find(trail => trail.id.toString() === trailId);
            setSelectedTrail(matchTrail);
            
            // ✅ Define matchData AFTER trail is set
            const matchData = matchDoc.data(); // Now safely used below
            
            await updateDoc(doc(db, 'matches', matchDoc.id), {
              'profile_ids.id2': user.uid,
              'status': 'pending',
              'participants': [matchData.profile_ids.id1, user.uid]
            });
            
    
            setMatches(prevMatches => prevMatches.map(match => 
                match.id === matchDoc.id 
                    ? {
                        ...match,
                        profile_ids: {
                            id1: selectedProfile.id,
                            id2: user.uid
                        },
                        status: 'pending',
                        compatibility: potentialMatches.find(m => m.profile.id === selectedProfile.id)?.compatibility || null
                    }
                    : match
            ));
            setSelectedProfile({ name: 'Your new hiking buddy' });
            setCurrentView('request-sent');
        } catch (error) {
            console.error("Error in handleSendRequest:", error);
        }
    };
    

    const handleViewTrailDetails = (trail, timing, source) => {
        console.log("App.js: handleViewTrailDetails called with:", { trail, timing, source });
        setSelectedTrail(trail);
        setCurrentView('trail-detail');
        setTrailViewSource(source || 'home');
        console.log("App.js: Navigation state updated:", { 
            selectedTrail: trail.id, 
            currentView: 'trail-detail',
            source: source || 'home'
        });
    };

    const handleViewProfile = React.useCallback((profile, options = {}) => { // ✅ Ensure options object is accepted
        if (!profile || !profile.id) {
             console.warn("Attempted to view profile with missing data:", profile);
             return;
        }
         console.log("App.js: handleViewProfile called with source:", options.from); // Added console log for debugging
        setSelectedProfile({ ...profile, isMatchProfile: options.isMatchProfile || false }); // Keep existing logic
         setSelectedProfileSource(options.from || 'unknown'); // ✅ Correctly set the source state based on options.from, default to 'unknown'
        setCurrentView('profile'); // Navigate to the profile page view
    }, []);
    const handleAcceptMatch = async (matchId, fromView = 'matches') => {
        try {
            const matchDocRef = doc(db, 'matches', matchId);
            const matchDoc = await getDoc(matchDocRef);
            const matchData = matchDoc.data();
            await updateDoc(matchDocRef, {
                status: 'confirmed',
                confirmedAt: new Date().toISOString(),
                participants: [matchData.profile_ids.id1, matchData.profile_ids.id2] // ✅ Add this
            });
            
            const [user1Doc, user2Doc] = await Promise.all([
                getDoc(doc(db, 'users', matchData.profile_ids.id1)),
                getDoc(doc(db, 'users', matchData.profile_ids.id2))
            ]);
            const trailDoc = await getDoc(doc(db, 'trails', matchData.trail_id));
            const user1Data = user1Doc.data();
            const user2Data = user2Doc.data();
            const activity = matchData.sport?.toLowerCase() || 'hiking';
            const user1Skill = user1Data.skills?.[activity] || 'not specified';
            const user2Skill = user2Data.skills?.[activity] || 'not specified';
            console.log(user1Data);
            console.log(user2Data);
            const emailPromises = [
                emailjs.send(
                    process.env.REACT_APP_EMAILJS_SERVICE_ID,
                    process.env.REACT_APP_EMAILJS_CONFIRMED_MATCH_TEMPLATE_ID,
                    {
                        to_email: user1Data.email,
                        to_name: user1Data.name,
                        partner_name: user2Data.name,
                        partner_bio: user2Data.bio || 'No bio provided.',
                        partner_skill_level: user2Skill,
                        trail_name: trailDoc.data().name,
                        start_time: new Date(matchData.start_time).toLocaleString(),
                        end_time: new Date(matchData.end_time).toLocaleString(),
                        location: trailDoc.data().location
                    }
                ),
                emailjs.send(
                    process.env.REACT_APP_EMAILJS_SERVICE_ID,
                    process.env.REACT_APP_EMAILJS_CONFIRMED_MATCH_TEMPLATE_ID,
                    {
                        to_email: user2Data.email,
                        to_name: user2Data.name,
                        partner_name: user1Data.name,
                        partner_bio: user1Data.bio || 'No bio provided.',
                        partner_skill_level: user1Skill,
                        trail_name: trailDoc.data().name,
                        start_time: new Date(matchData.start_time).toLocaleString(),
                        end_time: new Date(matchData.end_time).toLocaleString(),
                        location: trailDoc.data().location
                    }
                )
            ];
            
            console.log(emailPromises);
            await Promise.all(emailPromises);
            setMatches(prevMatches => 
                prevMatches.map(m => 
                    m.id === matchId ? { 
                        ...m, 
                        status: 'confirmed',
                        confirmedAt: new Date().toISOString()
                    } : m
                )
            );
            if (fromView === 'matches') {
                setCurrentView('matches-page');
            }
        } catch (error) {
            console.error("Error accepting match:", error);
        }
    };

    const handleCancelMatch = async (matchId) => {
        try {
            const matchDocRef = doc(db, 'matches', matchId);
            await updateDoc(matchDocRef, {
                status: 'cancelled'
            });
            setMatches(prevMatches => 
                prevMatches.filter(match => match.id !== matchId)
            );
        } catch (error) {
            console.error("Error cancelling match:", error);
        }
    };
    const handleCloseProfile = React.useCallback(() => { // ✅ This function does NOT need to accept the source argument from ProfileView anymore, as it will use the state variable.
        console.log("Closing profile view, navigating back based on source:", selectedProfileSource); // Log the state variable
    
        // Determine destination view based on the stored selectedProfileSource state
        if (selectedProfileSource === 'agenda') {
          setCurrentView('agenda'); // Go back to the agenda view
        } else if (selectedProfileSource && selectedProfileSource.startsWith('matches')) { // Covers matches_pending, matches_upcoming, matches_completed, matches_gallery
           // Navigate back to the general matches view. The state within MatchesView itself
           // will ideally restore the correct tab. We can't directly set the tab state from here.
          setCurrentView('matches-page');
        } else {
          setCurrentView('home'); // Default fallback if source is unknown or null
        }
    
        // ✅ Clear profile state *after* setting the view
        setSelectedProfile(null); // Clear the selected profile state
        setSelectedProfileSource(null); // Clear the source state
    
     }, [selectedProfileSource, setCurrentView, setSelectedProfile, setSelectedProfileSource]);
      

    const checkPastMatches = React.useCallback(async () => {
        if (!user || !matches.length) return;
        const now = new Date();
        const expiredMatches = matches.filter(match => {
            const startTime = match.start_time?.toDate?.() || new Date(match.start_time);
            const endTime = new Date(startTime);
            endTime.setHours(startTime.getHours() + match.duration);
            return (match.status === 'pending' || match.status === 'searching') && 
                   startTime < now;
        });
        if (expiredMatches.length > 0) {
            try {
                const cancelPromises = expiredMatches.map(async match => {
                    const matchRef = doc(db, 'matches', match.id);
                    const matchDoc = await getDoc(matchRef);
                    if (matchDoc.exists()) {
                        return updateDoc(matchRef, {
                            status: 'cancelled',
                            cancelReason: 'expired'
                        });
                    }
                    return Promise.resolve();
                });
                await Promise.all(cancelPromises);
                setMatches(prevMatches => 
                    prevMatches.filter(match => 
                        !expiredMatches.some(expired => expired.id === match.id)
                    )
                );
                if (expiredMatches.length === 1) {
                    console.log("A match has expired because the start time has passed.");
                } else {
                    console.log(`${expiredMatches.length} matches have expired because their start times have passed.`);
                }
            } catch (error) {
                console.error("Error cancelling expired matches:", error);
            }
        }
        const pastMatches = matches.filter(match => {
            const endTime = match.end_time?.toDate?.() || new Date(match.end_time);
            const isPastMatch = match.status === 'confirmed' && endTime < now;
            const userFeedback = match.feedback?.[user.uid];
            const userHasResponded = userFeedback && (userFeedback.skipped || userFeedback.attended !== null);
            const isUserInvolved = match.profile_ids.id1 === user.uid || match.profile_ids.id2 === user.uid;
            return isPastMatch && !userHasResponded && isUserInvolved;
        });
        if (pastMatches.length > 0) {
            setPastMatchToReview(pastMatches[0]);
        }
        const addFutureTrails = async () => {
            try {
                for (const match of expiredMatches) {
                    const trailDoc = await getDoc(doc(db, 'trails', match.trail_id));
                    if (!trailDoc.exists()) continue;
                    const originalTrail = trailDoc.data();
                    const originalStart = originalTrail.start_time instanceof Date 
                        ? originalTrail.start_time 
                        : originalTrail.start_time?.toDate?.()
                            ? originalTrail.start_time.toDate()
                            : new Date(originalTrail.start_time);
                    const originalEnd = originalTrail.end_time instanceof Date
                        ? originalTrail.end_time
                        : originalTrail.end_time?.toDate?.()
                            ? originalTrail.end_time.toDate()
                            : new Date(originalTrail.end_time);
                    const newStartTime = new Date(originalStart);
                    newStartTime.setDate(newStartTime.getDate() + 7);
                    const newEndTime = new Date(originalEnd);
                    newEndTime.setDate(newEndTime.getDate() + 7);
                    const newTrail = {
                        ...originalTrail,
                        start_time: newStartTime,
                        end_time: newEndTime
                    };
                    await addDoc(collection(db, 'trails'), newTrail);
                }
                if (expiredMatches.length > 0) {
                    const trailsSnapshot = await getDocs(collection(db, 'trails'));
                    const trailsData = trailsSnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                    setTrails(trailsData);
                }
            } catch (error) {
                console.error("Error adding future trails:", error);
            }
        };
        if (expiredMatches.length > 0) {
            await addFutureTrails();
        }
    }, [matches, user]);

    React.useEffect(() => {
        if (user) {
            checkPastMatches();
        }
    }, [user, matches, checkPastMatches]);

    const handleFeedbackSubmit = async (feedback) => {
        try {
            const matchDocRef = doc(db, 'matches', pastMatchToReview.id);
            const matchDoc = await getDoc(matchDocRef);
            const matchData = matchDoc.data();
            const updatedFeedback = {
                ...matchData.feedback || {},
                [user.uid]: {  
                    ...feedback,
                    submittedAt: new Date().toISOString()
                }
            };
            const bothUsersSubmitted = matchData.profile_ids.id1 in updatedFeedback && 
                                       matchData.profile_ids.id2 in updatedFeedback;
            await updateDoc(matchDocRef, {
                status: bothUsersSubmitted ? 'completed' : 'confirmed',
                feedback: updatedFeedback
            });
            setMatches(prevMatches => 
                prevMatches.map(m => 
                    m.id === pastMatchToReview.id 
                        ? {
                            ...m,
                            status: bothUsersSubmitted ? 'completed' : 'confirmed',
                            feedback: updatedFeedback
                        }
                        : m
                )
            );
            setPastMatchToReview(null);
            checkPastMatches();
        } catch (error) {
            console.error("Error submitting feedback:", error);
        }
    };

    const handleFeedbackSkip = async () => {
        try {
            const matchDocRef = doc(db, 'matches', pastMatchToReview.id);
            const matchDoc = await getDoc(matchDocRef);
            const matchData = matchDoc.data();
            const updatedFeedback = {
                ...matchData.feedback || {},
                [user.uid]: {  
                    skipped: true,
                    submittedAt: new Date().toISOString()
                }
            };
            const bothUsersResponded = matchData.profile_ids.id1 in updatedFeedback && 
                                       matchData.profile_ids.id2 in updatedFeedback;
            await updateDoc(matchDocRef, {
                status: bothUsersResponded ? 'completed' : 'confirmed',
                feedback: updatedFeedback
            });
            setMatches(prevMatches => 
                prevMatches.map(m => 
                    m.id === pastMatchToReview.id 
                        ? {
                            ...m,
                            status: bothUsersResponded ? 'completed' : 'confirmed',
                            feedback: updatedFeedback
                        }
                        : m
                )
            );
            setPastMatchToReview(null);
            checkPastMatches();
        } catch (error) {
            console.error("Error skipping feedback:", error);
        }
    };

    const handleFeedbackRequest = (match) => {
        setPastMatchToReview({
            ...match,
            currentUserId: user.uid
        });
    };

    const handleHelpClick = () => {
        setShowOnboarding(true);
    };

    React.useEffect(() => {
        if (user) {
            const checkOnboarding = async () => {
                const userDocRef = doc(db, 'users', user.uid);
                const userDoc = await getDoc(userDocRef);
                const hasSeenOnboarding = userDoc.data()?.hasSeenOnboarding;
                if (!hasSeenOnboarding) {
                    setShowOnboarding(true);
                }
            };
            checkOnboarding();
        }
    }, [user]);

    const handleOnboardingComplete = async () => {
        if (user) {
            const userDocRef = doc(db, 'users', user.uid);
            await setDoc(userDocRef, {
                hasSeenOnboarding: true,
                skills: user.skills || { hiking: 'beginner', running: 'beginner' }
            }, { merge: true }); // ✅ this prevents the "No document to update" error
        }
        setShowOnboarding(false);
    };
    const renderCurrentView = () => {
        switch (currentView) {
            case 'home':
                return <HomeView
                trails={trails}
                onTrailSelect={(trail) => {
                    if (!user) {
                        setRequireAuth(true); // ✅ triggers login modal
                    } else {
                        setSelectedTrail(trail);
                        setCurrentView('trail-detail');
                        setTrailViewSource('home');
                    }
                }}
                onProfileClick={(profile) => {
                    setSelectedProfile(profile);
                    setCurrentView('profile');
                }}
                user={user}
                />;
            case 'matching':
                return <MatchingView />;
                case 'no-matches':
                    return (
<NoMatchesView
  onChooseAnother={(destination) => setCurrentView(destination)}
  currentTrailId={selectedTrail?.id}
  currentUserId={user?.uid}
  onInvite={(buddyId) => {
    console.log("Invited user:", buddyId);
    // Possibly handle sending an invite
  }}
  user={user} // ✅ ADD THIS LINE
/>

                    );
                  
            case 'matches-page':
                return <MatchesView
                    matches={matches}
                    user={user}
                    onViewDetails={handleViewTrailDetails}
                    onViewProfile={handleViewProfile}
                    onAcceptMatch={handleAcceptMatch}
                    onCancelMatch={handleCancelMatch}
                    onFeedbackRequest={handleFeedbackRequest}
                    trails={trails}
                    profiles={profiles}
                />;
            case 'matches':
                return <MatchesGalleryView 
                    profiles={potentialMatches.map(match => ({
                        ...match.profile,
                        compatibility: match.compatibility
                    }))}
                    onProfileClick={(profile) => {
                        setSelectedProfile(profile);
                        setCurrentView('profile');
                    }}
                />;
            case 'profile':
                return <ProfileView 
                    profile={selectedProfile} 
                    onSendRequest={handleSendRequest} 
                   onBack={() => handleCloseProfile(selectedProfileSource)} 
                   source={selectedProfileSource}

                />;
            case 'request-sent':
                return <RequestSentView profile={selectedProfile} onOk={() => setCurrentView('home')} />;
            case 'match-accepted':
                return <MatchAcceptedView
                    profile={selectedProfile}
                    trail={selectedTrail}
                    matches={matches}
                    user={user}
                    onContinue={() => setCurrentView('matches-page')}
                />;
            case 'personal-profile':
                return <PersonalProfileView onSave={(updatedProfile) => {
                    setUser(prevUser => ({
                        ...prevUser,
                        ...updatedProfile
                    }));
                }} />;
            case 'agenda':
                return <AgendaView
                    onViewTrailDetails={handleViewTrailDetails}
                    onViewProfile={handleViewProfile}
                    onAcceptMatch={handleAcceptMatch}
                    onCancelMatch={handleCancelMatch}
                    onFeedbackRequest={handleFeedbackRequest}
                    matches={matches}
                    user={user}
                />;
            case 'trail-detail':
                // IMPORTANT: Use our new handler so that clicking "Find Hiking Buddy" marks the user as searching for the current trail and redirects.
                return <TrailDetailView 
                trail={selectedTrail} 
                onMatch={handleFindHikingBuddy}
                onBack={() => {
                    setCurrentView(trailViewSource === 'agenda' ? 'agenda' : 
                                  trailViewSource === 'matches' ? 'matches-page' : 'home');
                    if (trailViewSource !== 'agenda' && trailViewSource !== 'matches') {
                        setTrailViewSource('home');
                    }
                }}
                source={trailViewSource}
                user={user} // ✅ ADD THIS LINE
            />
            
            default:
                return <HomeView 
                    trails={trails}
                    onTrailSelect={(trail) => {
                        setSelectedTrail(trail);
                        setCurrentView('trail');
                    }}
                    onProfileClick={handleViewProfile}
                    user={user}
                />;
        }
    };

    const handleProfileUpdate = (updatedUserData) => {
        setUser(prevUser => ({
            ...prevUser,
            ...updatedUserData
        }));
    };

    const onViewProfile = (profile, options = {}) => {
        setSelectedProfile(profile);
        setSelectedProfileSource(options.from || 'matches');
        setShowProfile(true);
      };

    const isAdmin = user && ['Isaacvelezaguirre@gmail.com', 'gianmimaz@gmail.com', 'keerthi.dev.autsai@gmail.com', 'isawills@hotmail.com'].includes(user.email);

    return (
        <Router>
            <div className="min-h-screen bg-gray-50">
                {loading ? (
                    <div>Loading...</div>
                ) : requireAuth && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                        <LoginView 
              onLoginSuccess={() => setRequireAuth(false)} // Existing login success
              onClose={() => setRequireAuth(false)}
              // ✅ NEW PROP: Handle redirect after registration form completion
              onRegistrationCompleteRedirect={() => {
                  setRequireAuth(false);
                  setCurrentView('home');
              }}
              onProfileUpdate={handleProfileUpdate}
          />
                    </div>
                )}
                
                {showOnboarding && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                        <OnboardingView onComplete={handleOnboardingComplete} />
                    </div>
                )}
                
                <HeaderContainer
                    user={user}
                    onProfileClick={() => user ? setCurrentView('personal-profile') : setRequireAuth(true)}
                    onSignOut={async () => {
                        try {
                            await auth.signOut();
                            setUser(null);
                            setCurrentView('home');
                        } catch (error) {
                            console.error("Error signing out:", error);
                        }
                    }}
                    onHelpClick={handleHelpClick}
                    setRequireAuth={setRequireAuth}
                    isAdmin={isAdmin}
                    onLogoClick={() => setCurrentView('home')}
                />

                <div className="pt-16 pb-20">
                    <Routes>
                        <Route 
                            path="/admin" 
                            element={
                                <AdminRoute>
                                    <AdminTrailManager />
                                </AdminRoute>
                            } 
                        />
                        <Route 
                            path="/*" 
                            element={renderCurrentView()} 
                        />
                    </Routes>
                </div>

                <Navigation 
                    currentView={currentView}
                    onViewChange={setCurrentView}
                    user={user}
                    setRequireAuth={setRequireAuth}
                />

                {pastMatchToReview && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                        <PastMatchFeedbackModal
                            match={pastMatchToReview}
                            onSubmit={handleFeedbackSubmit}
                            onSkip={handleFeedbackSkip}
                            onClose={() => setPastMatchToReview(null)}
                        />
                    </div>
                )}
            </div>
        </Router>
    );
};

export default App;
